class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onFormChange = this.onFormChange.bind(this);
    this.onFormInput = this.onFormInput.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onFormInput);
    facetForm.addEventListener('change', this.onFormChange);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const rawSearchParams = event.state ? event.state.searchParams : window.location.search.slice(1);
      const searchParams = FacetFiltersForm.friendlyToTechnicalSearchParams(rawSearchParams);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = FacetFiltersForm.buildRenderUrl(searchParams, section.section);
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static buildRenderUrl(searchParams, sectionId) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);

    if (FacetFiltersForm.shouldUseFullPageFetch(searchParams)) {
      return `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    }

    return `${window.location.pathname}?section_id=${sectionId}${searchParams ? `&${searchParams}` : ''}`;
  }

  static shouldUseFullPageFetch(searchParams) {
    if (!searchParams) return false;
    return new URLSearchParams(searchParams).has('limit');
  }

  static normalizeSearchParams(searchParams) {
    const params = new URLSearchParams(typeof searchParams === 'string' ? searchParams : '');
    const normalized = new URLSearchParams();

    params.forEach((value, key) => {
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '' || trimmedValue == null) return;
      normalized.append(key, trimmedValue);
    });

    return normalized.toString();
  }

  static technicalToFriendlySearchParams(searchParams) {
    const params = new URLSearchParams(FacetFiltersForm.normalizeSearchParams(searchParams));
    const friendly = new URLSearchParams();
    const price = {
      min: '',
      max: '',
    };

    params.forEach((value, key) => {
      if (key === 'sort_by') {
        if (value !== 'manual') {
          friendly.append('sort', value);
        }
        return;
      }

      if (key === 'limit') {
        if (value !== '15') {
          friendly.append('count', value);
        }
        return;
      }

      if (key === 'filter.v.price.gte') {
        price.min = value;
        return;
      }

      if (key === 'filter.v.price.lte') {
        price.max = value;
        return;
      }

      if (key.startsWith('filter.')) {
        friendly.append(FacetFiltersForm.aliasFromTechnicalKey(key), FacetFiltersForm.friendlyValueFromTechnical(key, value));
        return;
      }

      friendly.append(key, value);
    });

    if (price.min || price.max) {
      const min = FacetFiltersForm.formatFriendlyNumber(price.min || '0');
      const max = FacetFiltersForm.formatFriendlyNumber(price.max || '');
      friendly.append('price', max ? `${min}-${max}` : `${min}+`);
    }

    return friendly.toString();
  }

  static friendlyToTechnicalSearchParams(searchParams) {
    const params = new URLSearchParams(FacetFiltersForm.normalizeSearchParams(searchParams));
    const technical = new URLSearchParams();

    params.forEach((value, key) => {
      if (key === 'sort') {
        technical.append('sort_by', value);
        return;
      }

      if (key === 'count') {
        technical.append('limit', value);
        return;
      }

      if (key === 'price') {
        if (value.includes('+')) {
          const min = value.replace('+', '');
          if (min) technical.append('filter.v.price.gte', min);
          return;
        }

        const [min = '', max = ''] = value.split('-');
        if (min !== '') technical.append('filter.v.price.gte', min);
        if (max !== '') technical.append('filter.v.price.lte', max);
        return;
      }

      if (!key.startsWith('filter.') && FacetFiltersForm.looksLikeFriendlyFilterKey(key)) {
        technical.append(
          FacetFiltersForm.technicalKeyFromAlias(key),
          FacetFiltersForm.technicalValueFromFriendly(key, value)
        );
        return;
      }

      technical.append(key, value);
    });

    return FacetFiltersForm.normalizeSearchParams(technical.toString());
  }

  static aliasFromTechnicalKey(key) {
    if (key === 'filter.v.availability') return 'availability';
    if (key === 'filter.p.t.category') return 'category';
    return key.replace(/^filter\.(p|v)\./, '').replace(/\./g, '_');
  }

  static technicalKeyFromAlias(alias) {
    if (alias === 'availability') return 'filter.v.availability';
    if (alias === 'category') return 'filter.p.t.category';
    return `filter.v.${alias.replace(/_/g, '.')}`;
  }

  static friendlyValueFromTechnical(key, value) {
    if (key === 'filter.v.availability') {
      if (value === '1') return 'in-stock';
      if (value === '0') return 'out-of-stock';
    }

    return FacetFiltersForm.formatFriendlyNumber(value);
  }

  static technicalValueFromFriendly(key, value) {
    if (key === 'availability') {
      if (value === 'in-stock') return '1';
      if (value === 'out-of-stock') return '0';
    }

    return value;
  }

  static formatFriendlyNumber(value) {
    if (value === '') return '';
    const number = Number(value);
    if (!Number.isNaN(number) && Number.isFinite(number)) {
      return Number.isInteger(number) ? String(number) : String(number);
    }

    return value;
  }

  static looksLikeFriendlyFilterKey(key) {
    return !['page', 'q', 'sort', 'count', 'price', 'section_id'].includes(key);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    if (typeof window.BCPriceRangeInit === 'function') window.BCPriceRangeInit(document);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    if (menuDrawer) menuDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    const friendlySearchParams = FacetFiltersForm.technicalToFriendlySearchParams(searchParams);
    history.pushState(
      { searchParams: friendlySearchParams },
      '',
      `${window.location.pathname}${friendlySearchParams && '?'.concat(friendlySearchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    formData.forEach((value, key) => {
      const normalizedValue = typeof value === 'string' ? value.trim() : value;
      if (normalizedValue === '' || normalizedValue == null) return;
      params.append(key, normalizedValue);
    });

    return FacetFiltersForm.normalizeSearchParams(params.toString());
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /**
   * Checkbox/radio inputs fire both 'input' and 'change' natively — this form
   * used to route every 'input' through the same 800ms debounce meant for
   * continuous controls (range slider drag, text search), so a single filter
   * checkbox click sat behind a fixed ~800ms wait before the request even
   * started. A checkbox click is one discrete, complete action with nothing
   * to wait out — submit it right away and keep the debounce only for inputs
   * that fire repeatedly in quick succession.
   */
  onFormInput(event) {
    const type = event.target && event.target.type;
    if (type === 'checkbox' || type === 'radio') {
      this.onSubmitHandler(event);
    } else {
      this.debouncedOnSubmit(event);
    }
  }

  onFormChange(event) {
    const target = event.target;
    if (!target || !target.name) return;

    if (target.name === 'limit') {
      event.preventDefault();
      const sortFilterForms = document.querySelectorAll('facet-filters-form form');
      const forms = [];

      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
      return;
    }

    if (target.tagName === 'SELECT') {
      this.onSubmitHandler(event);
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.friendlyToTechnicalSearchParams(window.location.search.slice(1));
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

document.addEventListener('DOMContentLoaded', () => {
  const currentSearchParams = window.location.search.slice(1);
  const technicalSearchParams = FacetFiltersForm.friendlyToTechnicalSearchParams(currentSearchParams);
  const friendlySearchParams = FacetFiltersForm.technicalToFriendlySearchParams(technicalSearchParams);
  const hasFriendlyAliases = currentSearchParams !== technicalSearchParams;

  if (hasFriendlyAliases && technicalSearchParams) {
    FacetFiltersForm.renderPage(technicalSearchParams, null, false);
  }

  if (currentSearchParams !== friendlySearchParams) {
    history.replaceState(
      { searchParams: friendlySearchParams },
      '',
      `${window.location.pathname}${friendlySearchParams ? `?${friendlySearchParams}` : ''}`
    );
  }
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
